import { useState } from 'react'
import { Button, Label, Input } from '@creanizer/ui'
import { X, Download, Edit2, Save as SaveIcon } from 'lucide-react'
import { usePlotStore } from '../../../../stores/plotStore'
import type { SavedPlot } from '@creanizer/types'
import type { CharacterInput, CharacterResult, SavedCharacter } from '../../../../types/character' // temp fix

interface PlotDetailProps {
  plot: SavedPlot
  onClose: () => void
}

export function PlotDetail({ plot, onClose }: PlotDetailProps) {
  const { updatePlotMemo, updatePlotTags, exportPlot } = usePlotStore()
  const [isEditingMemo, setIsEditingMemo] = useState(false)
  const [isEditingTags, setIsEditingTags] = useState(false)
  const [memo, setMemo] = useState(plot.memo || '')
  const [tags, setTags] = useState(plot.tags?.join(', ') || '')
  
  const { result, input } = plot
  const storyLength = input.storyLength === 'short' ? '短編（4-6章）' : 
                      input.storyLength === 'long' ? '長編（11-15章）' : '中編（7-10章）'

  const handleSaveMemo = () => {
    updatePlotMemo(plot.id, memo)
    setIsEditingMemo(false)
  }

  const handleSaveTags = () => {
    const tagArray = tags.split(',').map(t => t.trim()).filter(t => t)
    updatePlotTags(plot.id, tagArray)
    setIsEditingTags(false)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* ヘッダー */}
        <div className="border-b p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">{result.title}</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportPlot(plot.id)}
            >
              <Download className="h-4 w-4 mr-2" />
              エクスポート
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* コンテンツ */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* 生成情報 */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">生成日時:</span>
              <span className="ml-2">{new Date(plot.createdAt).toLocaleString('ja-JP')}</span>
            </div>
            <div>
              <span className="text-gray-600">物語の長さ:</span>
              <span className="ml-2">{storyLength}</span>
            </div>
            <div>
              <span className="text-gray-600">ジャンル:</span>
              <span className="ml-2">{input.genreCustom || input.genre}</span>
            </div>
            <div>
              <span className="text-gray-600">舞台:</span>
              <span className="ml-2">{input.stageCustom || input.stage}</span>
            </div>
            <div className="col-span-2">
              <span className="text-gray-600">キーワード:</span>
              <span className="ml-2">{result.keywords.join('、')}</span>
            </div>
            <div>
              <span className="text-gray-600">奇抜さレベル:</span>
              <span className="ml-2">{input.creativityLevel}/10</span>
            </div>
          </div>

          {/* 前提 */}
          <div>
            <h3 className="font-semibold mb-2">前提</h3>
            <p className="text-gray-700">{result.premise}</p>
          </div>

          {/* あらすじ */}
          <div>
            <h3 className="font-semibold mb-2">あらすじ</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{result.synopsis}</p>
          </div>

          {/* 章構成 */}
          {result.structure && result.structure.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">章構成</h3>
              <div className="space-y-3">
                {result.structure.map((chapter, i) => (
                  <div key={i} className="border-l-3 border-blue-400 pl-4">
                    <h4 className="font-medium">
                      第{chapter.number}章: {chapter.title}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">{chapter.summary}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 登場人物 */}
          {result.characters && result.characters.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">主要登場人物</h3>
              <div className="space-y-2">
                {result.characters.map((char, i) => (
                  <div key={i}>
                    <span className="font-medium">{char.name}:</span>
                    <span className="ml-2 text-gray-700">{char.role}</span>
                    {char.description && (
                      <p className="text-sm text-gray-600 ml-4">{char.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* テーマ */}
          {result.themes && result.themes.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">テーマ</h3>
              <div className="flex flex-wrap gap-2">
                {result.themes.map((theme, i) => (
                  <span key={i} className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                    {theme}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* メモ */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>メモ</Label>
              {!isEditingMemo ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditingMemo(true)}
                >
                  <Edit2 className="h-4 w-4 mr-1" />
                  編集
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSaveMemo}
                >
                  <SaveIcon className="h-4 w-4 mr-1" />
                  保存
                </Button>
              )}
            </div>
            {isEditingMemo ? (
              <textarea
                className="w-full p-2 border rounded-md"
                rows={3}
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="メモを入力..."
              />
            ) : (
              <p className="text-gray-700 p-2 bg-gray-50 rounded-md min-h-[60px]">
                {plot.memo || '（メモなし）'}
              </p>
            )}
          </div>

          {/* タグ */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>タグ</Label>
              {!isEditingTags ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditingTags(true)}
                >
                  <Edit2 className="h-4 w-4 mr-1" />
                  編集
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSaveTags}
                >
                  <SaveIcon className="h-4 w-4 mr-1" />
                  保存
                </Button>
              )}
            </div>
            {isEditingTags ? (
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="タグをカンマ区切りで入力..."
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                {plot.tags && plot.tags.length > 0 ? (
                  plot.tags.map((tag, i) => (
                    <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                      {tag}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500">（タグなし）</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}